import type {
  Position,
  TemplateAttribute,
} from '@/services/hooks/template_attributes'
import { isKonvaAnnotationTemplate } from './Annotations/annotationSchema'
import type { KonvaAnnotationTemplate } from './Annotations/annotationSchema'
import { toRenderableKonvaAnnotation } from './Annotations/legacyAnnotationMappers'
import {
  getAbsoluteYFromSubPage,
  getSubPagePositionFromAbsoluteY,
} from './Annotations/utils'

type TemplateAttributeUpdate = {
  data_type: TemplateAttribute<any>['data_type']
  payload: Record<string, unknown>
  templateAttributeId: string
}

type DocumentPageWithTemplateValues = {
  id?: string | null
  template_values?: Record<string, unknown> | null
}

const getDocumentSubPageIds = (documentPage: DocumentPageWithTemplateValues) =>
  (documentPage?.template_values?.subPages as Array<string>) ?? []

const getTemplateSubPageId = ({
  documentPage,
  template,
}: {
  documentPage: DocumentPageWithTemplateValues
  template: TemplateAttribute<any>
}) => {
  const documentSubPages = getDocumentSubPageIds(documentPage)

  return (
    template.template_values?.subPageId ??
    documentSubPages[0] ??
    (documentPage.id as string)
  )
}

export const getTemplateAttributePositionUpdate = ({
  delta,
  documentPage,
  template,
}: {
  delta: Position
  documentPage: DocumentPageWithTemplateValues
  template: TemplateAttribute<any>
}): TemplateAttributeUpdate => {
  const currentPosition = (template?.template_values?.position as Position) || {
    x: 0,
    y: 0,
  }
  const currentX = currentPosition?.x ?? 0
  const currentY = currentPosition?.y ?? 0
  const documentSubPages = getDocumentSubPageIds(documentPage)
  const currentSubPageId = getTemplateSubPageId({ documentPage, template })
  const absoluteY =
    getAbsoluteYFromSubPage({
      subPageId: currentSubPageId,
      relativeY: currentY,
      subPageIds: documentSubPages,
    }) + delta.y
  const nextSubPagePosition = getSubPagePositionFromAbsoluteY({
    absoluteY,
    fallbackSubPageId: currentSubPageId,
    subPageIds: documentSubPages,
  })

  return {
    data_type: template.data_type,
    payload: {
      subPageId: nextSubPagePosition.subPageId,
      position: {
        ...(template?.template_values?.position || {}),
        x: currentX + delta.x,
        y: nextSubPagePosition.relativeY,
      },
    },
    templateAttributeId: template.id as string,
  }
}

export const getAnnotationTemplateMoveUpdate = ({
  annotation,
  delta,
  documentPage,
  template,
}: {
  annotation: KonvaAnnotationTemplate
  delta: Position
  documentPage: DocumentPageWithTemplateValues
  template: TemplateAttribute<any>
}): TemplateAttributeUpdate => {
  const documentSubPages = getDocumentSubPageIds(documentPage)
  const values = template.template_values || {}
  const subPageId = values.subPageId ?? (documentPage.id as string)

  if (!isKonvaAnnotationTemplate(values)) {
    if (annotation.geometry.mode === 'bounds') {
      const currentPosition = values.position ?? { x: 0, y: 0 }
      const currentAbsolutePositionY = getAbsoluteYFromSubPage({
        subPageId,
        relativeY: currentPosition.y ?? 0,
        subPageIds: documentSubPages,
      })
      const nextSubPagePosition = getSubPagePositionFromAbsoluteY({
        absoluteY: currentAbsolutePositionY + delta.y,
        fallbackSubPageId: subPageId,
        subPageIds: documentSubPages,
      })

      return {
        data_type: template.data_type,
        payload: {
          ...values,
          subPageId: nextSubPagePosition.subPageId,
          position: {
            ...currentPosition,
            x: (currentPosition.x ?? 0) + delta.x,
            y: nextSubPagePosition.relativeY,
          },
        },
        templateAttributeId: template.id as string,
      }
    }

    const currentStartPoint = values.startPoint ?? { x: 0, y: 0 }
    const currentEndPoint = values.endPoint ?? currentStartPoint
    const currentPosition = values.position ?? {
      x: currentStartPoint.x,
      y: currentStartPoint.y,
    }
    const startPointAbsoluteY = getAbsoluteYFromSubPage({
      subPageId,
      relativeY: currentStartPoint.y ?? 0,
      subPageIds: documentSubPages,
    })
    const endPointAbsoluteY = getAbsoluteYFromSubPage({
      subPageId,
      relativeY: currentEndPoint.y ?? 0,
      subPageIds: documentSubPages,
    })
    const positionAbsoluteY = getAbsoluteYFromSubPage({
      subPageId,
      relativeY: currentPosition.y ?? 0,
      subPageIds: documentSubPages,
    })
    const nextSubPagePosition = getSubPagePositionFromAbsoluteY({
      absoluteY: startPointAbsoluteY + delta.y,
      fallbackSubPageId: subPageId,
      subPageIds: documentSubPages,
    })

    return {
      data_type: template.data_type,
      payload: {
        ...values,
        subPageId: nextSubPagePosition.subPageId,
        startPoint: {
          x: (currentStartPoint.x ?? 0) + delta.x,
          y: nextSubPagePosition.relativeY,
        },
        endPoint: {
          x: (currentEndPoint.x ?? 0) + delta.x,
          y: endPointAbsoluteY + delta.y - nextSubPagePosition.subPageOffset,
        },
        position: {
          ...currentPosition,
          x: (currentPosition.x ?? 0) + delta.x,
          y: positionAbsoluteY + delta.y - nextSubPagePosition.subPageOffset,
        },
      },
      templateAttributeId: template.id as string,
    }
  }

  const nextSubPagePosition = getSubPagePositionFromAbsoluteY({
    absoluteY:
      getAbsoluteYFromSubPage({
        subPageId,
        relativeY: annotation.geometry.y,
        subPageIds: documentSubPages,
      }) + delta.y,
    fallbackSubPageId: subPageId,
    subPageIds: documentSubPages,
  })

  return {
    data_type: template.data_type,
    payload: {
      ...values,
      subPageId: nextSubPagePosition.subPageId,
      geometry: {
        ...annotation.geometry,
        x: annotation.geometry.x + delta.x,
        y: nextSubPagePosition.relativeY,
      },
    },
    templateAttributeId: template.id as string,
  }
}

export const getTemplateAttributeMoveUpdate = ({
  delta,
  documentPage,
  template,
}: {
  delta: Position
  documentPage: DocumentPageWithTemplateValues
  template: TemplateAttribute<any>
}): TemplateAttributeUpdate => {
  if (template.data_type !== 'annotation') {
    return getTemplateAttributePositionUpdate({
      delta,
      documentPage,
      template,
    })
  }

  return getAnnotationTemplateMoveUpdate({
    annotation: toRenderableKonvaAnnotation(template.template_values as any),
    delta,
    documentPage,
    template,
  })
}

export const getKonvaAnnotationDragDelta = ({
  annotation,
  documentPage,
  position,
  template,
}: {
  annotation: KonvaAnnotationTemplate
  documentPage: DocumentPageWithTemplateValues
  position: Position
  template: TemplateAttribute<any>
}) => {
  const documentSubPages = getDocumentSubPageIds(documentPage)
  const templateValues = template.template_values || {}
  const currentSubPageId =
    templateValues.subPageId ?? (documentPage.id as string)
  const originalAbsoluteY = getAbsoluteYFromSubPage({
    subPageId: annotation.subPageId ?? currentSubPageId,
    relativeY: annotation.geometry.y,
    subPageIds: documentSubPages,
  })

  return {
    x: position.x - annotation.geometry.x,
    y: position.y - originalAbsoluteY,
  }
}
